import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../config/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';

const C = {
  bg:        '#020617',
  surface:   'rgba(15,23,42,0.98)',
  border:    'rgba(255,255,255,0.07)',
  borderCy:  'rgba(34,211,238,0.25)',
  borderAm:  'rgba(245,158,11,0.25)',
  text:      '#f1f5f9',
  textSub:   '#94a3b8',
  textFaint: '#475569',
  cyan:      '#22d3ee',
  amber:     '#f59e0b',
  emerald:   '#10b981',
  rose:      '#fb7185',
};

// ─── PRIMITIVES ───────────────────────────────────────────────────────────────
const Corners = ({ colorTL = C.cyan, colorBR = C.amber, size = 8 }) => (
  <>
    <span style={{ position: 'absolute', top: -1, left: -1, width: size, height: size,
      borderTop: `2px solid ${colorTL}`, borderLeft: `2px solid ${colorTL}` }} />
    <span style={{ position: 'absolute', bottom: -1, right: -1, width: size, height: size,
      borderBottom: `2px solid ${colorBR}`, borderRight: `2px solid ${colorBR}` }} />
  </>
);

const Dot = ({ color = C.cyan, pulse = false, size = 5 }) => (
  <span style={{
    display: 'inline-block', width: size, height: size, borderRadius: '50%',
    background: color, boxShadow: `0 0 7px ${color}`, flexShrink: 0,
    animation: pulse ? 'ob-pulse 2s ease-in-out infinite' : 'none',
  }} />
);

const M = ({ children, size = 10, color = C.textFaint, w = false, style = {} }) => (
  <span style={{
    fontFamily: "'Roboto Mono', monospace", fontSize: size,
    letterSpacing: '0.04em', color, fontWeight: w ? 700 : 400,
    lineHeight: 1, ...style,
  }}>
    {children}
  </span>
);

// ─── FEATURE ITEM ─────────────────────────────────────────────────────────────
function FeatureItem({ icon, label, desc, color, delay }) {
  const [hov, setHov] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 12px',
        border: `1px solid ${hov ? `${color}30` : C.border}`,
        background: hov ? `${color}04` : 'rgba(255,255,255,0.01)',
        transition: 'all 0.2s', cursor: 'default',
      }}>
      <div style={{
        width: 28, height: 28, flexShrink: 0,
        border: `1px solid ${color}28`,
        background: `${color}08`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color, position: 'relative',
      }}>
        <Corners colorTL={color} colorBR={color} size={4} />
        {icon}
      </div>
      <div>
        <M size={11} color={color} w style={{ display: 'block', marginBottom: 2 }}>{label}</M>
        <M size={10} color={C.textFaint}>{desc}</M>
      </div>
    </motion.div>
  );
}

// ─── CTA ITEM ─────────────────────────────────────────────────────────────────
function CtaItem({ color, label, onClick, delay }) {
  const [hov, setHov] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '11px 14px',
        border: `1px solid ${hov ? `${color}35` : C.border}`,
        background: hov ? `${color}06` : 'rgba(255,255,255,0.01)',
        cursor: 'pointer', transition: 'all 0.2s',
      }}>
      <Dot color={color} size={5} />
      <M size={11} color={hov ? C.text : C.textSub} style={{ flex: 1, transition: 'color 0.2s' }}>
        {label}
      </M>
      <M size={10} color={hov ? color : C.textFaint} style={{ transition: 'color 0.2s' }}>→</M>
    </motion.div>
  );
}

// ─── SLIDE CONTENT ────────────────────────────────────────────────────────────
function Slide0({ userName }) {
  return (
    <div>
      <M size={9} color={`${C.cyan}45`} style={{ display: 'block', marginBottom: 14 }}>
        NODE_01 · BIENVENUE
      </M>
      <h2 style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 700, fontSize: 'clamp(22px, 4vw, 30px)',
        letterSpacing: '-0.02em', lineHeight: 1.1,
        color: C.text, margin: '0 0 10px',
      }}>
        Votre espace{' '}
        <span style={{
          background: 'linear-gradient(135deg, #22d3ee, #38bdf8)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          PESA
        </span>
        <span style={{
          background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          CORE
        </span>{' '}
        est prêt.
      </h2>
      <p style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 300, fontSize: 13, lineHeight: 1.7,
        color: C.textSub, margin: '0 0 22px',
      }}>
        Bienvenue{userName ? `, ${userName}` : ''} — voici un tour rapide de votre tableau de bord.
        Chaque section est conçue pour s'adapter à vos efforts d'acquisition.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {[
          { label: '3', sub: 'SECTIONS', color: C.cyan },
          { label: '∞', sub: 'MODULES',  color: C.amber },
          { label: '∞→', sub: 'CERTIFS',  color: C.emerald },
        ].map((k, i) => (
          <motion.div key={k.sub}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.08 }}
            style={{
              padding: '12px', border: `1px solid ${C.border}`,
              background: 'rgba(255,255,255,0.015)', textAlign: 'center',
              position: 'relative', overflow: 'hidden',
            }}>
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 2,
              background: `linear-gradient(90deg, ${k.color}70, transparent)`,
            }} />
            <p style={{
              fontFamily: "'Roboto Mono', monospace",
              fontSize: 22, fontWeight: 700, color: k.color, margin: '0 0 4px',
            }}>
              {k.label}
            </p>
            <M size={9} color={C.textFaint}>{k.sub}</M>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function Slide1() {
  const items = [
    {
      color: C.cyan,
      label: 'PARCOURS',
      desc: 'Formations structurées · progression module par module',
      icon: (
        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path d="M4 6h16M4 12h16M4 18h10" />
        </svg>
      ),
    },
    {
      color: C.amber,
      label: 'Etudes de Cas',
      desc: 'Analyses de situations réelles · débrief personnalisé IH',
      icon: (
        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ),
    },
    {
      color: C.emerald,
      label: 'ATELIERS',
      desc: 'Outils pratiques · débloqués à la complétion',
      icon: (
        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <path d="M17 14v6M14 17h6" />
        </svg>
      ),
    },
  ];
  return (
    <div>
      <M size={9} color={`${C.cyan}45`} style={{ display: 'block', marginBottom: 14 }}>
        NODE_02 · NAVIGATION
      </M>
      <h2 style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 700, fontSize: 'clamp(22px, 4vw, 30px)',
        letterSpacing: '-0.02em', lineHeight: 1.1,
        color: C.text, margin: '0 0 10px',
      }}>
        3 zones{' '}
        <span style={{
          background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          clés
        </span>
      </h2>
      <p style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 300, fontSize: 13, lineHeight: 1.7,
        color: C.textSub, margin: '0 0 16px',
      }}>
        Chaque zone remplit un rôle précis dans votre parcours d'apprentissage.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((item, i) => (
          <FeatureItem key={item.label} {...item} delay={0.05 + i * 0.08} />
        ))}
      </div>
    </div>
  );
}

function Slide2({ onNavigate, onClose }) {
  const ctas = [
    { color: C.cyan,    label: 'Explorer les Parcours disponibles', to: '/parcours'},
    { color: C.amber,   label: 'Commencer une étude de cas', to: '/centre-intro'},
    { color: C.emerald, label: 'Découvrir les Ateliers', to: '/ateliers'},
  ];
  return (
    <div>
      <M size={9} color={`${C.cyan}45`} style={{ display: 'block', marginBottom: 14 }}>
        NODE_03 · DÉMARRER
      </M>
      <h2 style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 700, fontSize: 'clamp(22px, 4vw, 30px)',
        letterSpacing: '-0.02em', lineHeight: 1.1,
        color: C.text, margin: '0 0 10px',
      }}>
        Par où{' '}
        <span style={{
          background: 'linear-gradient(135deg, #22d3ee, #38bdf8)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          commencer ?
        </span>
      </h2>
      <p style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 300, fontSize: 13, lineHeight: 1.7,
        color: C.textSub, margin: '0 0 16px',
      }}>
        Vous pouvez changer de cap à tout moment. Pour débloquer votre premier parcours et vos ateliers, rendez-vous sur le dashboard et cliquez sur Etudes de Cas.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {ctas.map((c, i) => (
          <CtaItem
            key={c.to}
            color={c.color}
            label={c.label}
            delay={0.05 + i * 0.08}
            onClick={() => {}}
          />
        ))}
      </div>
    </div>
  );
}

function ResendButton({ email }) {
  const [sent, setSent]       = useState(false);
  const [sending, setSending] = useState(false);

  const handleResend = async () => {
    if (sent || sending) return;
    setSending(true);
    try {
      await api.post('/auth/resend-verification', { email });
      setSent(true);
    } catch {
      // silencieux — l'utilisateur peut réessayer
    } finally {
      setSending(false);
    }
  };

  return (
    <button
      onClick={handleResend}
      disabled={sent || sending}
      style={{
        background: 'rgba(2,6,23,0.15)',
        border: '1px solid rgba(2,6,23,0.25)',
        color: '#020617',
        fontFamily: 'monospace',
        fontSize: '0.62rem',
        fontWeight: 700,
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        padding: '6px 14px',
        cursor: sent || sending ? 'default' : 'pointer',
        alignSelf: 'center',
        opacity: sent || sending ? 0.6 : 1,
        transition: 'opacity 0.2s',
      }}
    >
      {sent ? '✓ Email renvoyé' : sending ? 'Envoi...' : 'Renvoyer l\'email'}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function OnboardingModal({ user, navigate, onDone }) {
  const { loading, refreshUser } = useAuth();

  const [visible, setVisible] = useState(false);
  const [cur, setCur] = useState(0);
  const TOTAL = 3;

  const decidedRef = useRef(false);

  useEffect(() => {
    if (loading || !user || decidedRef.current) return;

    decidedRef.current = true;

    const shouldShow = user.hasSeenOnboarding !== true;

    console.log('Onboarding check → hasSeenOnboarding:', user.hasSeenOnboarding, '→ shouldShow:', shouldShow);

    setVisible(shouldShow);
  }, [loading, user?._id, user?.hasSeenOnboarding]);   // dépendance plus stable

  const dismiss = useCallback(async () => {
    setVisible(false);

    try {
      await api.patch('/users/me', { hasSeenOnboarding: true });
      refreshUser?.();
    } catch (err) {
      console.error("Erreur mise à jour hasSeenOnboarding:", err);
    }

    if (onDone) onDone();
  }, [onDone]);

  const handleNavigate = useCallback((to) => {
    dismiss();
    navigate(to);
  }, [dismiss, navigate]);

  const next = () => {
    if (cur < TOTAL - 1) {
      setCur(c => c + 1);
    } else {
      dismiss();
    }
  };

  const prev = () => {
    if (cur > 0) setCur(c => c - 1);
  };

  const slides = [
    <Slide0 key="s0" userName={user?.prenom} />,
    <Slide1 key="s1" />,
    <Slide2 key="s2" onNavigate={handleNavigate} onClose={dismiss} />,
  ];

  if (!visible) return null;

  return (
    <AnimatePresence>
      {visible && (
        <> 
          
          
          
          {/* ── BACKDROP ── */}
          <motion.div
            key="ob-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={dismiss}
            style={{
              position: 'fixed', inset: 0, zIndex: 100,
              background: 'rgba(2,6,23,0.88)',
              backdropFilter: 'blur(6px)',
            }}
          />
 
          {/* ── MODAL ── */}
          <motion.div
            key="ob-modal"
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1,    y: 0  }}
            exit={{   opacity: 0, scale: 0.96,  y: 16 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28, mass: 0.9 }}
            style={{
              position: 'fixed',
              inset: 0,                    // top:0 left:0 right:0 bottom:0
              margin: 'auto',
              zIndex: 101,
              width: '90vw',
              maxWidth: 480,
              maxHeight: '92dvh',
              background: C.surface,
              border: `1px solid ${C.border}`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}>
 
            {/* Inline keyframes */}
            <style>{`
              @keyframes ob-pulse { 0%,100%{opacity:.4;transform:scale(1)} 50%{opacity:1;transform:scale(1.3)} }
              @keyframes ob-scan  { 0%{top:0%} 100%{top:100%} }
            `}</style>
 
            <Corners colorTL={C.cyan} colorBR={C.amber} size={9} />
 
            {/* Scan line */}
            <div style={{
              position: 'absolute', left: 0, right: 0, height: 1,
              background: `rgba(34,211,238,0.25)`,
              boxShadow: '0 0 10px rgba(34,211,238,0.4)',
              animation: 'ob-scan 4s linear infinite',
              pointerEvents: 'none', zIndex: 5,
            }} />
 
            {/* Ambient glow top-right */}
            <div style={{
              position: 'absolute', top: -60, right: -40, width: 200, height: 200,
              background: 'radial-gradient(circle, rgba(245,158,11,0.06), transparent 65%)',
              pointerEvents: 'none',
            }} />
 
            {/* ── TOP BAR ── */}
            <div style={{
              padding: '13px 18px',
              borderBottom: `1px solid ${C.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              position: 'relative', zIndex: 2,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Dot color={C.emerald} pulse size={5} />
                <M size={9} color={`${C.cyan}45`}>PESACORE_INIT</M>
                <span style={{ width: 1, height: 10, background: C.border, display: 'inline-block' }} />
                <M size={9} color="rgba(255,255,255,0.18)">
                  0{cur + 1} / 0{TOTAL}
                </M>
              </div>
              <button
                onClick={dismiss}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '3px 0', transition: 'opacity 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.6'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <M size={9} color={C.textFaint}>PASSER</M>
                <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                  <path d="M1 1l7 7M8 1L1 8" stroke={C.textFaint} strokeWidth="1.2" strokeLinecap="square" />
                </svg>
              </button>
            </div>
 
            {/* ── SLIDE AREA ── */}
            <div style={{ padding: '26px 24px 20px', minHeight: 300, position: 'relative', zIndex: 2 }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={cur}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0  }}
                  exit={{   opacity: 0, y: -8  }}
                  transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}>
                  {slides[cur]}
                </motion.div>
              </AnimatePresence>
            </div>
 
            {/* ── PROGRESS SEGMENTS ── */}
            <div style={{
              padding: '0 24px 14px',
              display: 'flex', gap: 5,
              position: 'relative', zIndex: 2,
            }}>
              {Array.from({ length: TOTAL }).map((_, i) => (
                <motion.div
                  key={i}
                  animate={{ background: i < cur ? C.cyan : i === cur ? C.amber : 'rgba(255,255,255,0.06)' }}
                  transition={{ duration: 0.4 }}
                  style={{
                    flex: 1, height: 2,
                    boxShadow: i === cur ? `0 0 8px ${C.amber}60` : 'none',
                  }}
                />
              ))}
            </div>
 
            {/* ── NAV ROW ── */}
            <div style={{
              padding: '0 24px 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              position: 'relative', zIndex: 2,
            }}>
              {/* PREV */}
              <motion.button
                animate={{ opacity: cur === 0 ? 0 : 1, pointerEvents: cur === 0 ? 'none' : 'auto' }}
                onClick={prev}
                style={{
                  background: 'none', cursor: 'pointer',
                  border: `1px solid ${C.border}`,
                  padding: '8px 14px',
                  display: 'flex', alignItems: 'center', gap: 6,
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                <M size={10} color={C.textFaint}>← RETOUR</M>
              </motion.button>
 
              {/* NEXT / FINISH */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={next}
                style={{
                  cursor: 'pointer',
                  border: `1px solid ${cur === TOTAL - 1 ? C.borderAm : 'rgba(245,158,11,0.3)'}`,
                  background: cur === TOTAL - 1
                    ? 'linear-gradient(135deg, rgba(245,158,11,0.14), rgba(34,211,238,0.08))'
                    : 'rgba(245,158,11,0.07)',
                  padding: '9px 18px',
                  display: 'flex', alignItems: 'center', gap: 8,
                  transition: 'all 0.2s',
                }}>
                <M size={10} color={C.amber} w>
                  {cur === TOTAL - 1 ? 'ACCÉDER AU DASHBOARD' : 'SUIVANT'}
                </M>
                <div style={{
                  width: 5, height: 5, borderRadius: '50%',
                  background: C.amber,
                  boxShadow: `0 0 7px ${C.amber}80`,
                  animation: cur === TOTAL - 1 ? 'ob-pulse 1.5s ease-in-out infinite' : 'none',
                }} />
              </motion.button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
} 